/**
 * The scan flow: capture -> process -> review -> confirm.
 *
 * Owns the worker and the OCR engine so both survive between scans — starting
 * the recogniser costs a model load, and paying it again on every retake would
 * make the second attempt slower than the first, exactly when the user is
 * already impatient.
 *
 * Nothing here writes to storage. The result is handed to the caller and, if
 * the caller drops it, it is gone.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { CameraCapture } from './CameraCapture';
import { ScanReview } from './ScanReview';
import { detectCapability, modelIsCached, passesFor } from '@/lib/scanner/capability';
import { TesseractOCREngine } from '@/lib/scanner/ocr-engine';
import {
  createImageWorker,
  runScan,
  ScanAbortedError,
  ScanQualityError,
} from '@/lib/scanner/pipeline';
import type { NutritionLabel, ScanProgress, ScanResult } from '@/lib/scanner/types';
import type { ScanEntry } from '@/lib/scanner/calculate';

type Phase = 'capture' | 'working' | 'review' | 'failed';

const EMPTY_ENTRY: ScanEntry = {
  price: null,
  currency: 'SGD',
  packAmount: null,
  packUnit: 'g',
  nickname: null,
};

export function ScanPanel({
  currency,
  onDone,
  onClose,
  debug = false,
}: {
  currency: string;
  onDone: (label: NutritionLabel, entry: ScanEntry) => void;
  onClose: () => void;
  debug?: boolean;
}): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>('capture');
  const [progress, setProgress] = useState<ScanProgress | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [entry, setEntry] = useState<ScanEntry>({ ...EMPTY_ENTRY, currency });
  const [modelReady, setModelReady] = useState<boolean | null>(null);

  const workerRef = useRef<Worker | null>(null);
  const engineRef = useRef<TesseractOCREngine | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const capability = useRef(detectCapability()).current;

  useEffect(() => {
    void modelIsCached().then(setModelReady);
  }, []);

  // Everything is torn down on unmount: the worker, the recogniser, and any
  // scan still running. A camera scan left holding memory after the page moves
  // on is the kind of leak that shows up as a crashed tab an hour later.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      workerRef.current?.terminate();
      workerRef.current = null;
      void engineRef.current?.terminate();
      engineRef.current = null;
    };
  }, []);

  const scan = useCallback(
    async (image: ImageData) => {
      setPhase('working');
      setFailure(null);
      setProgress({ stage: 'assessing', fraction: 0, note: 'Checking the photo…' });

      workerRef.current ??= createImageWorker();
      engineRef.current ??= new TesseractOCREngine();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const scanned = await runScan(image, engineRef.current, workerRef.current, {
          onProgress: setProgress,
          signal: controller.signal,
          maxPasses: passesFor(capability),
          debug,
        });
        setResult(scanned);
        setPhase('review');
        setModelReady(true);
      } catch (err) {
        if (err instanceof ScanAbortedError) {
          setPhase('capture');
          return;
        }
        if (err instanceof ScanQualityError) {
          // A photo we could not read is a normal outcome, not an error —
          // the advice is the useful part.
          setResult(err.result);
          setFailure(err.message);
          setPhase('failed');
          return;
        }
        setFailure(
          err instanceof Error ? err.message : 'Something went wrong while reading the photo.',
        );
        setPhase('failed');
      }
    },
    [capability, debug],
  );

  if (!capability.canScan) {
    return (
      <div className="scan-panel">
        <p className="scan-issue is-error">{capability.reason}</p>
        <button type="button" className="btn" onClick={onClose}>
          Close
        </button>
      </div>
    );
  }

  return (
    <div className="scan-panel">
      <div className="scan-panel-head">
        <h2>Scan a label</h2>
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {phase === 'capture' && (
        <>
          {modelReady === false && (
            // Said before the first scan, not during it: on mobile data this
            // is a decision the user should get to make.
            <p className="scan-issue">
              The first scan downloads about 8 MB of reader and language data. After that the
              scanner works with no connection at all.
            </p>
          )}
          <CameraCapture onCapture={(img) => void scan(img)} onCancel={onClose} />
        </>
      )}

      {phase === 'working' && (
        <div className="scan-working">
          <p className="scan-stage-note" aria-live="polite">
            {progress?.note ?? 'Working…'}
          </p>
          <div
            className="scan-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round((progress?.fraction ?? 0) * 100)}
          >
            <span style={{ width: `${Math.round((progress?.fraction ?? 0) * 100)}%` }} />
          </div>
          <button
            type="button"
            className="btn"
            onClick={() => abortRef.current?.abort()}
          >
            Cancel
          </button>
        </div>
      )}

      {phase === 'failed' && (
        <div className="scan-failed">
          <h3>Could not read that photo</h3>
          <p>{failure}</p>
          {result && (
            <ul className="scan-quality-list">
              {Object.entries(result.quality.quality)
                .filter(([k]) => k !== 'overall')
                .map(([key, score]) => (
                  <li key={key} className={score < 0.5 ? 'is-poor' : undefined}>
                    {QUALITY_LABELS[key] ?? key}
                    <span>{Math.round((score as number) * 100)}%</span>
                  </li>
                ))}
            </ul>
          )}
          <div className="scan-actions">
            <button type="button" className="btn btn-primary" onClick={() => setPhase('capture')}>
              Try again
            </button>
            <button type="button" className="btn" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      )}

      {phase === 'review' && result && (
        <>
          <ScanReview
            result={result}
            entry={entry}
            currency={currency}
            onEntryChange={setEntry}
            onConfirm={(label) => {
              onDone(label, entry);
              setEntry({ ...EMPTY_ENTRY, currency });
              setResult(null);
              setPhase('capture');
            }}
            onRetake={() => setPhase('capture')}
            onCancel={onClose}
          />
          {debug && result.debug && <ScanDebug result={result} />}
        </>
      )}
    </div>
  );
}

const QUALITY_LABELS: Record<string, string> = {
  resolution: 'Detail',
  sharpness: 'Focus',
  brightness: 'Lighting',
  contrast: 'Contrast',
  glare: 'Glare',
  perspective: 'Angle',
};

/**
 * The development view.
 *
 * OCR problems have to be seen to be diagnosed — a wrong number tells you
 * nothing about whether the crop, the threshold or the row grouping caused it.
 * Rendered only when explicitly enabled, never in the normal flow.
 */
function ScanDebug({ result }: { result: ScanResult }): React.JSX.Element {
  const debug = result.debug!;
  return (
    <details className="scan-debug">
      <summary>Debug</summary>
      <p>
        Region:{' '}
        {debug.region
          ? `${Math.round(debug.region.width)}x${Math.round(debug.region.height)} at ` +
            `${Math.round(debug.region.x)},${Math.round(debug.region.y)} ` +
            `(confidence ${debug.region.confidence.toFixed(2)})`
          : 'whole frame'}
      </p>
      <p>Variants: {debug.variants.map((v) => `${v.name} ${v.width}x${v.height}`).join(', ')}</p>
      <p>
        Passes:{' '}
        {debug.passes
          .map((p) => `${p.variant} ${(p.overallConfidence * 100).toFixed(0)}%`)
          .join(', ')}
      </p>
      <table className="scan-debug-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Amount</th>
            <th>DV</th>
          </tr>
        </thead>
        <tbody>
          {debug.rows.map((r, i) => (
            <tr key={`${r.label}-${i}`}>
              <td>{r.label}</td>
              <td>{r.amount}</td>
              <td>{r.dailyValue}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <pre className="scan-debug-text">
        {debug.passes[0]?.lines.map((l) => l.text).join('\n') ?? ''}
      </pre>
    </details>
  );
}
